import {
  keepPreviousData,
  useQuery,
  type UseQueryOptions
} from '@tanstack/react-query';
import {
  MeetingListItem,
  MeetingStatuses
} from '@/components/page-components/meetings/types';
import type { PaginatedResponse } from '../types';
import { useApiClient } from '../context';
import { meetingsQueryKeys } from './query-keys';
import type { MeetingBundle, MeetingListParams } from './types';

export const MEETING_PROCESSING_POLL_MS = 5000;

export function useMeetingsListQuery(
  params?: MeetingListParams,
  options?: Omit<
    UseQueryOptions<PaginatedResponse<MeetingListItem>>,
    'queryKey' | 'queryFn'
  >
) {
  const api = useApiClient();
  return useQuery({
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const data = query.state.data;
      const items = Array.isArray(data) ? data : data?.data;
      const hasInProgress =
        Array.isArray(items) &&
        items.some((m) => m.status === MeetingStatuses.InProgress);
      return hasInProgress ? MEETING_PROCESSING_POLL_MS : false;
    },
    ...options,
    queryKey: meetingsQueryKeys.list(params),
    queryFn: () => api.meetings.list(params)
  });
}

export function useMeetingQuery(
  id: string | undefined,
  options?: Omit<UseQueryOptions<MeetingBundle>, 'queryKey' | 'queryFn'>
) {
  const api = useApiClient();
  return useQuery({
    refetchInterval: (query) => {
      const status = query.state.data?.meeting?.status;
      return status === MeetingStatuses.InProgress
        ? MEETING_PROCESSING_POLL_MS
        : false;
    },
    ...options,
    queryKey:
      id != null
        ? meetingsQueryKeys.detail(id)
        : [...meetingsQueryKeys.details(), 'disabled'],
    queryFn: () => {
      if (id == null) throw new Error('meeting id is required');
      return api.meetings.get(id);
    },
    enabled: (options?.enabled ?? true) && id != null && id.length > 0
  });
}
